/**
 * Passwordless sign-in — step 2. The email screen (login / create-account) has
 * already requested a code; this collects the 6-digit code, verifies it, and
 * signs the person in. A brand-new email creates a society-agnostic account on
 * verify (with the name + phone carried from create-account); the home guard
 * then routes a fresh resident to add their first flat.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Pressable } from 'react-native';

import { space } from '@bero/tokens';
import { homeFor } from '@/lib/homeFor';
import { useSession } from '@/lib/session';
import { useT } from '@/i18n';
import { AppText, Button, Field, Input, Screen, TopBar } from '@/ui';

const RESEND_SECONDS = 30;

export default function VerifyCodeScreen() {
  const { t } = useT();
  const { verifyCode, requestCode } = useSession();
  const params = useLocalSearchParams<{ email?: string; name?: string; phone?: string }>();
  const email = (params.email ?? '').trim().toLowerCase();
  const name = (params.name ?? '').trim() || undefined;
  const phone = (params.phone ?? '').trim() || undefined;

  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(RESEND_SECONDS);

  React.useEffect(() => { if (!email) router.replace('/login'); }, [email]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submit = async (value: string) => {
    if (busy || value.length < 6) return;
    setBusy(true);
    setError('');
    try {
      const { user } = await verifyCode(email, value, name, phone);
      router.replace(homeFor(user) as '/(tabs)/home');
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(status === 429 ? t('vc_err_rate') : status === 401 || status === 400 ? t('vc_err_invalid') : t('vc_err_generic'));
      setCode('');
      setBusy(false);
    }
  };

  const onChange = (v: string) => {
    const digits = v.replace(/[^0-9]/g, '').slice(0, 6);
    setCode(digits);
    if (error) setError('');
    if (digits.length === 6) void submit(digits);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setCooldown(RESEND_SECONDS);
    setError('');
    try { await requestCode(email); } catch { /* always-200; ignore */ }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('vc_title')} onBack={() => router.replace('/login')} />
      <Screen padded style={{ paddingTop: space.s6, gap: space.s5 }}>
        <AppText variant="bodyMd" tone="secondary">{t('vc_sent_to', { email })}</AppText>
        <Field label={t('vc_code_label')} error={error || undefined}>
          <Input
            value={code}
            onChangeText={onChange}
            keyboardType="number-pad"
            autoComplete="one-time-code"
            textContentType="oneTimeCode"
            placeholder="000000"
            autoFocus
            hasError={!!error}
            style={{ fontSize: 26, letterSpacing: 10, textAlign: 'center', minHeight: 58 }}
          />
        </Field>
        <Button title={t('vc_verify')} size="lg" loading={busy} disabled={code.length < 6} onPress={() => void submit(code)} />
        <Pressable accessibilityRole="link" onPress={() => void resend()} disabled={cooldown > 0} hitSlop={8} style={{ alignSelf: 'center', marginTop: space.s2, opacity: cooldown > 0 ? 0.5 : 1 }}>
          <AppText variant="bodySm" weight="500" tone={cooldown > 0 ? 'tertiary' : 'brand'}>
            {cooldown > 0 ? t('vc_resend_in', { s: cooldown }) : t('vc_resend')}
          </AppText>
        </Pressable>
      </Screen>
    </Screen>
  );
}
